import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowRight, Linkedin, Plus, Trash2, ImagePlus, Loader2, Upload, Building2, MapPin, Maximize2, Users, TrendingUp } from "lucide-react";
import { motion, useInView, useReducedMotion, useScroll, useTransform, animate as fmAnimate } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icon";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const NAV = [
  { id: "about", label: "About" },
  { id: "stand-for", label: "What We Stand For" },
  { id: "journey", label: "Our Journey" },
  { id: "founders", label: "Our Founders" },
  { id: "ecosystem", label: "Our Brand" },
];

function esc(s) {
  return String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Inline-editable text. In edit mode it's contentEditable and commits onBlur so
// typing never triggers a React re-render (keeps the cursor stable).
export function Txt({ edit, path, value, as: As = "span", className, placeholder, testid }) {
  if (!edit) return <As className={className} data-testid={testid}>{value}</As>;
  return (
    <As
      contentEditable
      suppressContentEditableWarning
      data-testid={testid}
      data-placeholder={placeholder || "Type here…"}
      onBlur={(e) => edit.set(path, e.currentTarget.innerText)}
      className={cn("cwz-editable outline-none rounded-md focus:ring-2 focus:ring-primary/60 hover:bg-primary/5 focus:bg-primary/5 cursor-text transition-colors", className)}
      dangerouslySetInnerHTML={{ __html: esc(value) }}
    />
  );
}

export function EditImage({ edit, path, value, wrapClass, imgClass, alt, placeholderLabel = "image" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      edit.set(path, data.url);
      toast.success("Image updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className={cn("relative group/img", wrapClass)}>
      {value
        ? <img src={value} alt={alt || ""} className={imgClass} />
        : (edit ? <div className={cn("w-full h-full flex items-center justify-center text-slate-300 text-xs", imgClass)}>{`Add ${placeholderLabel}`}</div> : null)}
      {edit && (
        <>
          <button type="button" onClick={() => ref.current?.click()} disabled={busy}
            className="absolute inset-0 flex items-center justify-center gap-2 bg-slate-900/55 text-white text-sm font-medium opacity-0 group-hover/img:opacity-100 transition-opacity">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} {busy ? "Uploading…" : "Replace image"}
          </button>
          <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" hidden onChange={pick} />
        </>
      )}
    </div>
  );
}

// Hover toolbar for a repeatable item (move + delete).
export function ItemTools({ edit, path, index, count, vertical }) {
  if (!edit) return null;
  return (
    <div className="absolute -top-3 right-2 z-20 flex items-center gap-0.5 bg-white border border-slate-200 shadow-sm rounded-full px-1 py-0.5 opacity-0 group-hover/item:opacity-100 transition-opacity">
      <button type="button" title="Move back" disabled={index === 0} onClick={() => edit.move(path, index, -1)} className="p-1 text-slate-500 hover:text-primary disabled:opacity-30">{vertical ? <ChevronLeft className="w-3.5 h-3.5 rotate-90" /> : <ChevronLeft className="w-3.5 h-3.5" />}</button>
      <button type="button" title="Move forward" disabled={index === count - 1} onClick={() => edit.move(path, index, 1)} className="p-1 text-slate-500 hover:text-primary disabled:opacity-30">{vertical ? <ChevronRight className="w-3.5 h-3.5 rotate-90" /> : <ChevronRight className="w-3.5 h-3.5" />}</button>
      <button type="button" title="Delete" onClick={() => edit.remove(path, index)} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
    </div>
  );
}

export function AddBtn({ edit, path, blank, label }) {
  if (!edit) return null;
  return (
    <button type="button" onClick={() => edit.add(path, blank)} data-testid={`about-add-${path}`}
      className="inline-flex items-center gap-1.5 rounded-full border-2 border-dashed border-primary/40 text-primary text-sm font-semibold px-4 py-2 hover:bg-primary/5 transition-colors">
      <Plus className="w-4 h-4" /> {label}
    </button>
  );
}

function SubNav() {
  const go = (id) => { const el = document.getElementById(id); if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: "smooth" }); };
  return (
    <div className="sticky top-16 z-20 bg-white/85 backdrop-blur border-b border-slate-100">
      <div className="container-cz flex gap-1.5 overflow-x-auto no-scrollbar py-3">
        {NAV.map((n) => (
          <button key={n.id} onClick={() => go(n.id)} data-testid={`about-nav-${n.id}`}
            className="shrink-0 text-sm font-medium text-slate-600 hover:text-primary px-3.5 py-1.5 rounded-full hover:bg-sky-50 transition-colors whitespace-nowrap">{n.label}</button>
        ))}
      </div>
    </div>
  );
}

function Hero({ hero, edit }) {
  const stats = hero.stats || [];
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#eef3ff] via-[#f4f7ff] to-white border-b border-slate-100">
      <div className="container-cz py-16 sm:py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <Txt edit={edit} path="hero.eyebrow" value={hero.eyebrow} as="p" className="eyebrow mb-4 inline-block" placeholder="Eyebrow" />
          <Txt edit={edit} path="hero.title" value={hero.title} as="h1" testid="about-hero-title" className="block font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.02]" placeholder="Page title" />
          <Txt edit={edit} path="hero.subtitle" value={hero.subtitle} as="p" className="text-lg text-slate-600 mt-6 max-w-xl whitespace-pre-line" placeholder="Subtitle" />
          {(stats.length > 0 || edit) && (
            <div className="flex flex-wrap gap-x-8 gap-y-5 mt-10 items-start">
              {stats.map((s, i) => (
                <div key={i} className="relative group/item" data-testid={`about-hero-stat-${i}`}>
                  <ItemTools edit={edit} path="hero.stats" index={i} count={stats.length} />
                  <Txt edit={edit} path={`hero.stats.${i}.value`} value={s.value} as="p" className="block font-display text-3xl sm:text-4xl font-extrabold text-primary" placeholder="12+" />
                  <Txt edit={edit} path={`hero.stats.${i}.label`} value={s.label} as="p" className="block text-xs sm:text-sm text-slate-500 mt-1" placeholder="Label" />
                </div>
              ))}
              <AddBtn edit={edit} path="hero.stats" blank={{ value: "", label: "" }} label="Add stat" />
            </div>
          )}
        </div>
        <EditImage edit={edit} path="hero.image" value={hero.image} alt={hero.title}
          wrapClass="rounded-[28px] overflow-hidden shadow-cz aspect-[4/3.4] bg-slate-100"
          imgClass="w-full h-full object-cover" placeholderLabel="hero image" />
      </div>
    </section>
  );
}

function About({ about, edit }) {
  const paras = about.paragraphs || [];
  return (
    <section id="about" className="container-cz py-16 sm:py-20 max-w-4xl scroll-mt-24" data-testid="about-section">
      <Txt edit={edit} path="about.eyebrow" value={about.eyebrow} as="p" className="eyebrow mb-3 inline-block" placeholder="Eyebrow" />
      <Txt edit={edit} path="about.title" value={about.title} as="h2" className="block font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mb-6" placeholder="Heading" />
      {paras.map((p, i) => (
        <div key={i} className="relative group/item">
          <ItemTools edit={edit} path="about.paragraphs" index={i} count={paras.length} vertical />
          <Txt edit={edit} path={`about.paragraphs.${i}`} value={p} as="p" className={cn("text-slate-600 leading-relaxed whitespace-pre-line", i === 0 ? "text-lg" : "mt-4")} placeholder="Paragraph text" />
        </div>
      ))}
      <div className="mt-4"><AddBtn edit={edit} path="about.paragraphs" blank={""} label="Add paragraph" /></div>
    </section>
  );
}

function StandFor({ data, edit }) {
  const cards = data.cards || [];
  return (
    <section id="stand-for" className="bg-slate-50 border-y border-slate-100 scroll-mt-24" data-testid="about-standfor">
      <div className="container-cz py-16 sm:py-20">
        <Txt edit={edit} path="stand_for.eyebrow" value={data.eyebrow} as="p" className="eyebrow mb-3 inline-block" placeholder="Eyebrow" />
        <Txt edit={edit} path="stand_for.title" value={data.title} as="h2" className="block font-display text-3xl sm:text-4xl font-extrabold text-slate-900" placeholder="Heading" />
        <Txt edit={edit} path="stand_for.subtitle" value={data.subtitle} as="p" className="text-lg text-slate-600 mt-3 max-w-2xl" placeholder="Subtitle" />
        <div className="grid md:grid-cols-3 gap-6 mt-10 items-start">
          {cards.map((c, i) => (
            <div key={i} className="relative group/item card-cz p-7 bg-white" data-testid={`about-standfor-card-${i}`}>
              <ItemTools edit={edit} path="stand_for.cards" index={i} count={cards.length} />
              <span className="w-12 h-12 rounded-2xl bg-sky-50 text-primary flex items-center justify-center mb-5"><Icon name={c.icon} className="w-6 h-6" /></span>
              {edit && <Txt edit={edit} path={`stand_for.cards.${i}.icon`} value={c.icon} as="p" className="block text-[11px] text-slate-400 mb-2" placeholder="icon name (e.g. target)" />}
              <Txt edit={edit} path={`stand_for.cards.${i}.title`} value={c.title} as="h3" className="block font-display text-lg font-bold text-slate-900 leading-snug" placeholder="Statement" />
              <Txt edit={edit} path={`stand_for.cards.${i}.body`} value={c.body} as="p" className="text-sm text-slate-500 mt-3 leading-relaxed whitespace-pre-line" placeholder="Description" />
            </div>
          ))}
          {edit && <div className="flex items-center justify-center min-h-[160px]"><AddBtn edit={edit} path="stand_for.cards" blank={{ icon: "star", title: "", body: "" }} label="Add card" /></div>}
        </div>
      </div>
    </section>
  );
}

function Journey({ data, edit }) {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 320, behavior: "smooth" });
  const items = data.milestones || [];
  return (
    <section id="journey" className="container-cz py-16 sm:py-20 scroll-mt-24" data-testid="about-journey">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <Txt edit={edit} path="journey.eyebrow" value={data.eyebrow} as="p" className="eyebrow mb-3 inline-block" placeholder="Eyebrow" />
          <Txt edit={edit} path="journey.title" value={data.title} as="h2" className="block font-display text-3xl sm:text-4xl font-extrabold text-slate-900" placeholder="Heading" />
          <Txt edit={edit} path="journey.subtitle" value={data.subtitle} as="p" className="text-lg text-slate-600 mt-3 max-w-2xl" placeholder="Subtitle" />
        </div>
        <div className="hidden sm:flex gap-2 shrink-0">
          <button onClick={() => scroll(-1)} data-testid="about-journey-prev" className="w-11 h-11 rounded-full border border-slate-200 text-slate-500 hover:border-primary hover:text-primary flex items-center justify-center transition-colors"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={() => scroll(1)} data-testid="about-journey-next" className="w-11 h-11 rounded-full border border-slate-200 text-slate-500 hover:border-primary hover:text-primary flex items-center justify-center transition-colors"><ChevronRight className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="relative mt-10">
        <div className="absolute left-0 right-0 top-[26px] h-px bg-slate-200 hidden sm:block" aria-hidden="true" />
        <div ref={ref} className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 no-scrollbar" data-testid="about-journey-track">
          {items.map((m, i) => (
            <div key={i} className="snap-start shrink-0 w-[270px] sm:w-[300px]" data-testid={`about-journey-item-${i}`}>
              <div className="flex items-center gap-3">
                <span className="w-[14px] h-[14px] rounded-full bg-primary ring-4 ring-sky-100 shrink-0" />
                <Txt edit={edit} path={`journey.milestones.${i}.year`} value={m.year} as="span" className="font-display text-2xl font-extrabold text-primary" placeholder="2024" />
              </div>
              <div className="relative group/item card-cz p-5 mt-4">
                <ItemTools edit={edit} path="journey.milestones" index={i} count={items.length} />
                {(m.image || edit) && (
                  <EditImage edit={edit} path={`journey.milestones.${i}.image`} value={m.image} alt={m.title}
                    wrapClass="rounded-xl overflow-hidden aspect-[16/10] mb-4 bg-slate-100" imgClass="w-full h-full object-cover" placeholderLabel="photo" />
                )}
                <Txt edit={edit} path={`journey.milestones.${i}.title`} value={m.title} as="h3" className="block font-display text-base font-bold text-slate-900" placeholder="Milestone title" />
                <Txt edit={edit} path={`journey.milestones.${i}.description`} value={m.description} as="p" className="text-sm text-slate-500 mt-2 leading-relaxed whitespace-pre-line" placeholder="Description" />
              </div>
            </div>
          ))}
          {edit && <div className="shrink-0 w-[270px] flex items-center justify-center"><AddBtn edit={edit} path="journey.milestones" blank={{ year: "", title: "", description: "", image: "" }} label="Add milestone" /></div>}
        </div>
      </div>
    </section>
  );
}

function Founders({ data, edit }) {
  const people = data.people || [];
  return (
    <section id="founders" className="bg-slate-50 border-y border-slate-100 scroll-mt-24" data-testid="about-founders">
      <div className="container-cz py-16 sm:py-20">
        <Txt edit={edit} path="founders.eyebrow" value={data.eyebrow} as="p" className="eyebrow mb-3 inline-block" placeholder="Eyebrow" />
        <Txt edit={edit} path="founders.title" value={data.title} as="h2" className="block font-display text-3xl sm:text-4xl font-extrabold text-slate-900" placeholder="Heading" />
        <Txt edit={edit} path="founders.subtitle" value={data.subtitle} as="p" className="text-lg text-slate-600 mt-3 max-w-2xl" placeholder="Subtitle" />
        <div className="grid md:grid-cols-2 gap-6 mt-10 items-start">
          {people.map((p, i) => (
            <div key={i} className="relative group/item card-cz p-6 bg-white flex flex-col sm:flex-row gap-5" data-testid={`about-founder-${i}`}>
              <ItemTools edit={edit} path="founders.people" index={i} count={people.length} />
              <EditImage edit={edit} path={`founders.people.${i}.photo`} value={p.photo} alt={p.name}
                wrapClass="w-full sm:w-40 shrink-0 rounded-2xl overflow-hidden aspect-square bg-slate-100" imgClass="w-full h-full object-cover" placeholderLabel="photo" />
              <div className="flex-1">
                <Txt edit={edit} path={`founders.people.${i}.name`} value={p.name} as="h3" className="block font-display text-xl font-bold text-slate-900" placeholder="Full name" />
                <Txt edit={edit} path={`founders.people.${i}.role`} value={p.role} as="p" className="text-sm text-primary font-semibold mt-0.5" placeholder="Role" />
                <Txt edit={edit} path={`founders.people.${i}.bio`} value={p.bio} as="p" className="text-sm text-slate-500 mt-3 leading-relaxed whitespace-pre-line" placeholder="Short bio" />
                {edit ? (
                  <div className="mt-4 flex items-center gap-2 text-sm text-primary"><Linkedin className="w-4 h-4 shrink-0" /><Txt edit={edit} path={`founders.people.${i}.linkedin`} value={p.linkedin} as="span" className="block flex-1 font-semibold" placeholder="LinkedIn URL (optional)" /></div>
                ) : (p.linkedin && (
                  <a href={p.linkedin} target="_blank" rel="noreferrer" aria-label={`${p.name || "Founder"} on LinkedIn`} data-testid={`about-founder-linkedin-${i}`} className="mt-4 inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-white transition-colors"><Linkedin className="w-4 h-4" /></a>
                ))}
              </div>
            </div>
          ))}
          {edit && <div className="flex items-center justify-center min-h-[160px]"><AddBtn edit={edit} path="founders.people" blank={{ photo: "", name: "", role: "", bio: "", linkedin: "" }} label="Add founder" /></div>}
        </div>
      </div>
    </section>
  );
}

function Ecosystem({ data, edit, openLead }) {
  return (
    <section id="ecosystem" className="container-cz py-16 sm:py-20 scroll-mt-24" data-testid="about-ecosystem">
      <Txt edit={edit} path="ecosystem.eyebrow" value={data.eyebrow} as="p" className="eyebrow mb-3 inline-block" placeholder="Eyebrow" />
      <Txt edit={edit} path="ecosystem.title" value={data.title} as="h2" className="block font-display text-3xl sm:text-4xl font-extrabold text-slate-900 max-w-3xl" placeholder="Heading" />
      <Txt edit={edit} path="ecosystem.subtitle" value={data.subtitle} as="p" className="text-lg text-slate-600 mt-4 max-w-2xl" placeholder="Subtitle" />
      <div className="card-cz p-8 sm:p-10 mt-10 bg-gradient-to-br from-[#eef3ff] to-white flex flex-col items-start gap-4">
        <Txt edit={edit} path="ecosystem.cta_title" value={data.cta_title} as="h3" className="block font-display text-2xl sm:text-3xl font-bold text-slate-900" placeholder="CTA title" />
        <Txt edit={edit} path="ecosystem.cta_subtitle" value={data.cta_subtitle} as="p" className="text-slate-600 whitespace-pre-line" placeholder="CTA subtitle" />
        <div className="flex flex-wrap gap-3 mt-2">
          <Button data-testid="about-cta-tour" onClick={() => openLead && openLead("tour")} className="rounded-full h-12 px-7 gap-2">Book a Tour <ArrowRight className="w-4 h-4" /></Button>
          <Button data-testid="about-cta-quote" onClick={() => openLead && openLead("quote")} variant="outline" className="rounded-full h-12 px-7 border-slate-300">Get a Quote</Button>
        </div>
      </div>
    </section>
  );
}

const BAR_SHADES = ["#c7d2fe", "#aec3fc", "#8aa9f8", "#5f8bf3", "#3f74ee", "#2f6bef"];

const CHART_H = 400;
const BAR_BASE = 20;
const BAR_MAX = 316;
function barHeight(v, maxVal) { return BAR_BASE + (v / (maxVal || 1)) * (BAR_MAX - BAR_BASE); }
function buildPaths(years, maxVal, H) {
  const n = years.length || 1;
  if (!years.length) return { solid: "", dashed: "", pts: [] };
  const pts = years.map((y, i) => {
    const v = (y.is_current ? (y.projected_value || y.value) : y.value) || 0;
    const px = barHeight(v, maxVal);
    return { x: ((i + 0.5) / n) * 100, y: 100 - (px / CHART_H) * 100 };
  });
  const ci = years.findIndex((y) => y.is_current);
  // Catmull-Rom → cubic bezier so the growth line is ONE continuous smooth curve.
  const bez = (i) => {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1x = p1.x + (p2.x - p0.x) / 6, c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6, c2y = p2.y - (p3.y - p1.y) / 6;
    return `C${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
  };
  const solidEnd = ci > 0 ? ci - 1 : n - 1;
  let solid = `M${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < solidEnd; i++) solid += " " + bez(i);
  let dashed = "";
  if (ci > 0) {
    dashed = `M${pts[solidEnd].x} ${pts[solidEnd].y}`;
    for (let i = solidEnd; i < ci; i++) dashed += " " + bez(i);
  }
  return { solid, dashed, pts };
}

function CountUp({ label, play }) {
  const m = String(label || "").match(/([\d,]+(?:\.\d+)?)/);
  const target = m ? parseFloat(m[1].replace(/,/g, "")) : 0;
  const [val, setVal] = useState(play ? 0 : target);
  useEffect(() => {
    if (!play || !m) { setVal(target); return; }
    const controls = fmAnimate(0, target, { duration: 0.6, ease: "easeOut", onUpdate: (v) => setVal(v) });
    return () => controls.stop();
  }, [play, target]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!m) return <>{label}</>;
  const decimals = (m[1].split(".")[1] || "").length;
  const disp = decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString("en-IN");
  return <>{label.slice(0, m.index)}{disp}{label.slice(m.index + m[1].length)}</>;
}

function InfoRow({ icon: I, children }) {
  return <div className="flex items-center gap-2 text-[13px] text-slate-600 whitespace-nowrap"><I className="w-3.5 h-3.5 text-slate-400 shrink-0" />{children}</div>;
}

function BgSilhouettes() {
  return (
    <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 1200 400" fill="none" aria-hidden="true">
      <g fill="#c7d2fe" opacity="0.3">
        <rect x="40" y="270" width="60" height="130" /><rect x="120" y="230" width="50" height="170" />
        <rect x="980" y="250" width="60" height="150" /><rect x="1060" y="205" width="55" height="195" /><rect x="1130" y="280" width="45" height="120" />
      </g>
    </svg>
  );
}

function TopLabels({ y, i, edit, play }) {
  if (y.is_current) {
    return (
      <div className="flex flex-col items-center justify-end gap-1 w-full text-center">
        <span className="bg-white shadow-sm rounded-full px-3 py-1 text-xs font-bold text-primary inline-flex items-center gap-1 whitespace-nowrap">
          Expected <span className="text-emerald-500">▲</span>{edit ? <Txt edit={edit} path={`growth.years.${i}.growth`} value={y.growth} className="min-w-[20px] inline-block" /> : <span>{y.growth}</span>}
        </span>
        {edit && <span className="font-display font-extrabold text-base sm:text-lg text-primary whitespace-nowrap"><Txt edit={edit} path={`growth.years.${i}.projected`} value={y.projected} /></span>}
        {edit && <span className="text-[11px] text-slate-400 whitespace-nowrap">Achieved <Txt edit={edit} path={`growth.years.${i}.revenue`} value={y.revenue} className="inline" /></span>}
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-end gap-1 w-full text-center">
      {y.growth_type === "base"
        ? <span className="bg-white shadow-sm rounded-full px-3 py-1 text-xs font-bold text-slate-400">—</span>
        : <span className="bg-white shadow-sm rounded-full px-3 py-1 text-xs font-bold text-emerald-600 inline-flex items-center gap-1 whitespace-nowrap"><span className="text-emerald-500">▲</span>{edit ? <Txt edit={edit} path={`growth.years.${i}.growth`} value={y.growth} className="min-w-[20px] inline-block" /> : <span>{y.growth}</span>}</span>}
      {edit && <span className="font-display font-extrabold text-base sm:text-lg text-primary whitespace-nowrap"><Txt edit={edit} path={`growth.years.${i}.revenue`} value={y.revenue} /></span>}
    </div>
  );
}

function BarCol({ y, i, maxVal, H, shouldAnim, inView, spd }) {
  const topVal = (y.is_current ? (y.projected_value || y.value) : y.value) || 0;
  const barPx = barHeight(topVal, maxVal);
  const clip = "polygon(0 16%, 50% 0, 100% 16%, 100% 100%, 0 100%)";
  const anim = {
    initial: shouldAnim ? { height: 0 } : false,
    animate: shouldAnim ? (inView ? { height: barPx } : { height: 0 }) : { height: barPx },
    transition: { duration: 0.55 / spd, ease: "easeOut", delay: shouldAnim ? i * 0.05 : 0 },
  };
  if (y.is_current) {
    const achievedPx = barPx * ((y.value || 0) / (y.projected_value || y.value || 1));
    return (
      <motion.div className="w-[64%] max-w-[80px] self-end relative" style={{ clipPath: clip, background: "rgba(30,58,138,0.18)" }} {...anim} data-testid={`about-growth-bar-${i}`}>
        <div className="absolute bottom-0 left-0 right-0" style={{ height: achievedPx, background: "#1e3a8a" }} />
      </motion.div>
    );
  }
  return <motion.div className="w-[64%] max-w-[80px] self-end" style={{ clipPath: clip, background: BAR_SHADES[Math.min(i, BAR_SHADES.length - 1)] }} {...anim} data-testid={`about-growth-bar-${i}`} />;
}

function GrowthJourney({ data, edit }) {
  const reduce = useReducedMotion();
  const settings = data.settings || {};
  const years = data.years || [];
  const n = years.length || 1;
  const H = CHART_H;
  const spd = Number(settings.speed) || 1;
  const shouldAnim = settings.animate !== false && !reduce && !edit;
  const parallaxOn = settings.parallax !== false && !reduce && !edit;
  const chartRef = useRef(null);
  const sectionRef = useRef(null);
  const inView = useInView(chartRef, { once: true, amount: 0.2 });
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end start"] });
  const charY = useTransform(scrollYProgress, [0, 1], [26, -26]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -40]);
  const maxVal = Math.max(...years.map((y) => (y.is_current ? (y.projected_value || y.value) : y.value) || 0), 1);
  const { solid, dashed, pts } = buildPaths(years, maxVal, H);
  const ci = years.findIndex((y) => y.is_current);

  return (
    <section ref={sectionRef} id="growth" className="relative overflow-hidden bg-gradient-to-b from-[#eef3ff] via-white to-white border-y border-slate-100 scroll-mt-24" data-testid="about-growth">
      <motion.div style={parallaxOn ? { y: bgY } : undefined} className="pointer-events-none absolute inset-0 opacity-60"><BgSilhouettes /></motion.div>
      <div className="container-cz py-16 sm:py-20 relative">
        <div className="grid lg:grid-cols-[minmax(220px,300px)_1fr] gap-8 lg:gap-10 items-end">
          <div>
            <Txt edit={edit} path="growth.eyebrow" value={data.eyebrow} as="p" className="eyebrow mb-3 inline-block" placeholder="Eyebrow" />
            <Txt edit={edit} path="growth.title" value={data.title} as="h2" className="block font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight" placeholder="Heading" />
            {(data.intro || edit) && <Txt edit={edit} path="growth.intro" value={data.intro} as="p" className="text-slate-600 mt-5 leading-relaxed max-w-md whitespace-pre-line" placeholder="Intro paragraph" />}
            <motion.div style={parallaxOn ? { y: charY } : undefined} className="mt-8 max-w-[210px]">
              <EditImage edit={edit} path="growth.character_image" value={data.character_image} alt="CoworkZen growth"
                wrapClass="w-full" imgClass="w-full h-auto object-contain" placeholderLabel="character" />
            </motion.div>
          </div>

          <div ref={chartRef} className="overflow-x-auto no-scrollbar pb-2" data-testid="about-growth-chart">
            <div className="min-w-[680px] lg:min-w-0">
              <div className="relative flex gap-3 sm:gap-4 items-end" style={{ height: H }}>
                <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <motion.path d={solid} fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke"
                    initial={shouldAnim ? { pathLength: 0 } : false} animate={shouldAnim ? (inView ? { pathLength: 1 } : { pathLength: 0 }) : { pathLength: 1 }} transition={{ duration: 1.3 / spd, ease: "easeInOut" }} />
                  {dashed && <motion.path d={dashed} fill="none" stroke="#2563eb" strokeWidth="2.5" strokeDasharray="4 4" strokeLinecap="round" vectorEffect="non-scaling-stroke"
                    initial={shouldAnim ? { pathLength: 0 } : false} animate={shouldAnim ? (inView ? { pathLength: 1 } : { pathLength: 0 }) : { pathLength: 1 }} transition={{ duration: 0.6 / spd, ease: "easeInOut", delay: shouldAnim ? 1.3 / spd : 0 }} />}
                </svg>

                {pts.map((p, i) => (
                  <motion.span key={i} className="absolute z-10 w-2.5 h-2.5 rounded-full bg-primary ring-2 ring-white shadow" style={{ left: `${p.x}%`, top: `${p.y}%`, transform: "translate(-50%,-50%)" }}
                    initial={shouldAnim ? { scale: 0 } : false} animate={shouldAnim ? (inView ? { scale: 1 } : { scale: 0 }) : { scale: 1 }} transition={{ delay: shouldAnim ? 0.3 + i * 0.12 : 0, type: "spring", stiffness: 400, damping: 20 }} />
                ))}

                {years.map((y, i) => (
                  <div key={i} className="flex-1 min-w-[150px] lg:min-w-0 flex flex-col items-center justify-end relative">
                    <div className="mb-2 w-full flex justify-center relative z-20"><TopLabels y={y} i={i} edit={edit} play={shouldAnim ? inView : false} /></div>
                    <BarCol y={y} i={i} maxVal={maxVal} H={H} shouldAnim={shouldAnim} inView={inView} spd={spd} />
                  </div>
                ))}
              </div>

              <div className="flex gap-3 sm:gap-4 items-start mt-4">
                {years.map((y, i) => (
                  <div key={i} className="flex-1 min-w-[150px] lg:min-w-0">
                    <motion.div className="relative group/item card-cz px-3.5 py-4 sm:px-4 sm:py-5 h-full transition-shadow hover:shadow-cz"
                      initial={shouldAnim ? { opacity: 0, y: 16 } : false} animate={shouldAnim ? (inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }) : { opacity: 1, y: 0 }} transition={{ delay: shouldAnim ? 0.15 + i * 0.05 : 0, duration: 0.4 / spd }}
                      whileHover={shouldAnim ? { y: -4 } : undefined} data-testid={`about-growth-year-${i}`}>
                      {edit && <ItemTools edit={edit} path="growth.years" index={i} count={n} />}
                      <div className="text-center mb-4">
                        <Txt edit={edit} path={`growth.years.${i}.fy`} value={y.fy} as="span" className="font-display font-extrabold text-primary" placeholder="FY" />
                        {y.is_current && <span className="block text-[11px] text-slate-400">(Expected)</span>}
                      </div>
                      <div className="space-y-3">
                        <InfoRow icon={Building2}><Txt edit={edit} path={`growth.years.${i}.centers`} value={y.centers} className="inline" placeholder="Centers" /></InfoRow>
                        <InfoRow icon={MapPin}><Txt edit={edit} path={`growth.years.${i}.hubs`} value={y.hubs} className="inline" placeholder="Hubs" /></InfoRow>
                        {(y.area || edit) && <InfoRow icon={Maximize2}><Txt edit={edit} path={`growth.years.${i}.area`} value={y.area} className="inline" placeholder="Area (sq ft)" /></InfoRow>}
                        <InfoRow icon={Users}><Txt edit={edit} path={`growth.years.${i}.seats`} value={y.seats} className="inline" placeholder="Seats" /></InfoRow>
                      </div>
                    </motion.div>
                  </div>
                ))}
              </div>
              {edit && <div className="mt-4"><AddBtn edit={edit} path="growth.years" blank={{ fy: "FY", revenue: "₹0", value: 0, growth: "", growth_type: "up", centers: "", hubs: "", area: "", seats: "", is_current: false, projected: "", projected_value: 0, milestone: false }} label="Add year" /></div>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Renders all About Us sections. Pass `edit` (an object with set/add/remove/move)
// to turn on inline WYSIWYG editing; omit it for the public page.
export default function AboutSections({ data, edit, openLead }) {
  return (
    <>
      <GrowthJourney data={data.growth || {}} edit={edit} />
      <SubNav />
      <About about={data.about || {}} edit={edit} />
      <StandFor data={data.stand_for || {}} edit={edit} />
      <Journey data={data.journey || {}} edit={edit} />
      <Founders data={data.founders || {}} edit={edit} />
      <Ecosystem data={data.ecosystem || {}} edit={edit} openLead={openLead} />
    </>
  );
}
